/**
 * MyActions – Personal corrective-action queue
 *
 * Shows only actions assigned to the currently signed-in user.
 * Enterprise-level close-out flow:
 *   assigned → in_progress → completed → (verified) → closed
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, Clock, AlertTriangle, ArrowRight,
  Search, CalendarDays, Shield,
  Loader2, ChevronRight, RotateCcw,
  CircleDot, Inbox, Timer, CalendarClock, Send,
} from 'lucide-react';
import { Card, CardContent } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Textarea } from '@shared/components/ui/textarea';
import { Label } from '@shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@shared/components/ui/dialog';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { ENV } from '@shared/config/env';

// ─── Status Configuration ───────────────────────────────────────────
const STATUS_CONFIG = {
  assigned:              { label: 'Assigned',             color: 'bg-orange-500/10 text-orange-700 border-orange-300', dot: 'bg-orange-500', icon: CircleDot,     next: 'in_progress',        nextLabel: 'Start Work' },
  in_progress:           { label: 'In Progress',          color: 'bg-amber-500/10 text-amber-700 border-amber-300',   dot: 'bg-amber-500',  icon: Clock,         next: 'completed',          nextLabel: 'Mark Complete' },
  completed:             { label: 'Completed',            color: 'bg-blue-500/10 text-blue-700 border-blue-300',      dot: 'bg-blue-500',   icon: CheckCircle2,  next: null,                 nextLabel: null },
  verification_pending:  { label: 'Pending Verification', color: 'bg-purple-500/10 text-purple-700 border-purple-300', dot: 'bg-purple-500', icon: Shield,        next: null,                 nextLabel: null },
  verified:              { label: 'Verified',             color: 'bg-emerald-500/10 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500', icon: Shield,    next: 'closed',             nextLabel: 'Close Out' },
  rejected:              { label: 'Rejected',             color: 'bg-red-500/10 text-red-700 border-red-300',         dot: 'bg-red-500',    icon: RotateCcw,     next: 'in_progress',        nextLabel: 'Rework' },
  closed:                { label: 'Closed',               color: 'bg-gray-500/10 text-gray-600 border-gray-300',      dot: 'bg-gray-400',   icon: CheckCircle2,  next: null,                 nextLabel: null },
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-600 text-white' },
  high:     { label: 'High',     color: 'bg-red-500 text-white' },
  medium:   { label: 'Medium',   color: 'bg-amber-500 text-white' },
  low:      { label: 'Low',      color: 'bg-blue-500 text-white' },
};

const ACTION_TYPE_LABELS = {
  immediate_correction: 'Immediate Correction',
  corrective_action:    'Corrective Action',
  preventive_action:    'Preventive Action',
  improvement_action:   'Improvement Action',
};

// ─── Helpers ────────────────────────────────────────────────────────
function daysUntilDue(dueDate) {
  if (!dueDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due - now) / 86400000);
}

function dueBadge(dueDate) {
  const days = daysUntilDue(dueDate);
  if (days === null) return null;
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800/40' };
  if (days === 0) return { label: 'Due today', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800/40' };
  if (days <= 3) return { label: `${days}d left`, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800/40' };
  return { label: `${days}d left`, cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700' };
}

// ─── Component ──────────────────────────────────────────────────────
export default function MyActions() {
  const { user } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'all' | specific status
  const [selectedAction, setSelectedAction] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Extension request state
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [extensionAction, setExtensionAction] = useState(null);
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionRequests, setExtensionRequests] = useState({});
  const [submittingExtension, setSubmittingExtension] = useState(false);

  const loadActions = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('audit_actions')
        .select(`
          *,
          audit_findings (
            id, finding_title, finding_description, finding_type, severity, status,
            audits ( id, audit_title, audit_reference, audit_date )
          )
        `)
        .eq('assigned_to', user.id)
        .order('due_date', { ascending: true });

      if (error) throw error;
      setActions(data || []);
    } catch (err) {
      console.error('Error loading actions:', err);
      toast({ title: 'Error', description: 'Failed to load actions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadActions(); }, [loadActions]);

  // ── Load extension requests ─────────────────────────────────
  const loadExtensionRequests = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('action_extension_requests')
        .select('*')
        .eq('requested_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Group by action_id — keep the latest request for each
      const map = {};
      (data || []).forEach(req => {
        if (!map[req.action_id]) map[req.action_id] = req;
      });
      setExtensionRequests(map);
    } catch (err) {
      console.error('Error loading extension requests:', err);
    }
  }, [user?.id]);

  useEffect(() => { loadExtensionRequests(); }, [loadExtensionRequests]);

  // ── Request extension ───────────────────────────────────────
  const openExtensionDialog = (action) => {
    setExtensionAction(action);
    // Default to 7 days from current due date
    const base = action.due_date ? new Date(action.due_date) : new Date();
    setExtensionDate(new Date(base.getTime() + 7 * 86400000).toISOString().slice(0, 10));
    setExtensionReason('');
    setShowExtensionDialog(true);
  };

  const handleRequestExtension = async () => {
    if (!extensionAction || !extensionDate || !extensionReason.trim()) return;
    try {
      setSubmittingExtension(true);
      const { error } = await supabase
        .from('action_extension_requests')
        .insert({
          action_id: extensionAction.id,
          requested_by: user.id,
          original_due_date: extensionAction.due_date,
          requested_due_date: extensionDate,
          reason: extensionReason.trim(),
          status: 'pending',
        });

      if (error) throw error;

      // Fire-and-forget: notify the assigner via email
      try {
        if (extensionAction.assigned_by) {
          const { data: assignerData } = await supabase
            .from('users')
            .select('email, first_name, surname')
            .eq('id', extensionAction.assigned_by)
            .single();

          if (assignerData?.email) {
            const { data: authData } = await supabase.auth.getSession();
            const currentUserName = [user?.first_name, user?.surname].filter(Boolean).join(' ') || user?.email || 'A team member';
            const finding = extensionAction.audit_findings;
            const audit = finding?.audits;
            fetch(ENV.SUPABASE.edgeFn('notify-extension-request'), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authData.session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                type: 'requested',
                to_email: assignerData.email,
                to_name: [assignerData.first_name, assignerData.surname].filter(Boolean).join(' ') || assignerData.email,
                from_name: currentUserName,
                action_title: extensionAction.action_title,
                finding_title: finding?.finding_title || '',
                audit_reference: audit?.audit_reference || '',
                original_due_date: extensionAction.due_date,
                requested_due_date: extensionDate,
                reason: extensionReason.trim(),
              }),
            }).catch(err => console.warn('Extension notification email failed (non-blocking):', err));
          }
        }
      } catch (notifyErr) {
        console.warn('Could not send extension notification email:', notifyErr);
      }

      toast({
        title: 'Extension Requested',
        description: 'Your request has been submitted for review',
      });

      setShowExtensionDialog(false);
      setExtensionAction(null);
      setExtensionDate('');
      setExtensionReason('');
      loadExtensionRequests();
    } catch (err) {
      console.error('Error requesting extension:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmittingExtension(false);
    }
  };

  // ── Advance status ──────────────────────────────────────────
  const handleAdvanceStatus = async (action, targetStatus) => {
    try {
      setSaving(true);
      const updates = { status: targetStatus };

      if (targetStatus === 'in_progress') {
        updates.started_at = new Date().toISOString();
      }
      if (targetStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
        updates.completed_by = user.id;
        updates.completion_notes = notes || null;
        // If verification required, move to verification_pending instead
        if (action.requires_verification) {
          updates.status = 'verification_pending';
        }
      }
      if (targetStatus === 'closed') {
        updates.closed_at = new Date().toISOString();
        updates.closed_by = user.id;
      }

      const { error } = await supabase
        .from('audit_actions')
        .update(updates)
        .eq('id', action.id);

      if (error) throw error;

      toast({
        title: 'Action Updated',
        description: `Status changed to ${STATUS_CONFIG[updates.status]?.label || targetStatus}`,
      });
      setNotes('');
      setSelectedAction(null);
      loadActions();
    } catch (err) {
      console.error('Error updating action:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Filtering ───────────────────────────────────────────────
  const openStatuses = ['assigned', 'in_progress', 'rejected'];
  const filtered = actions.filter(a => {
    const matchesSearch = !search ||
      a.action_title?.toLowerCase().includes(search.toLowerCase()) ||
      a.action_description?.toLowerCase().includes(search.toLowerCase()) ||
      a.audit_findings?.finding_title?.toLowerCase().includes(search.toLowerCase()) ||
      a.audit_findings?.audits?.audit_reference?.toLowerCase().includes(search.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === 'open') {
      matchesStatus = openStatuses.includes(a.status);
    } else if (statusFilter !== 'all') {
      matchesStatus = a.status === statusFilter;
    }

    return matchesSearch && matchesStatus;
  });

  // ── Stats ───────────────────────────────────────────────────
  const totalOpen = actions.filter(a => openStatuses.includes(a.status)).length;
  const overdue = actions.filter(a => openStatuses.includes(a.status) && daysUntilDue(a.due_date) < 0).length;
  const dueSoon = actions.filter(a => openStatuses.includes(a.status) && daysUntilDue(a.due_date) >= 0 && daysUntilDue(a.due_date) <= 3).length;
  const completedCount = actions.filter(a => ['completed', 'verification_pending', 'verified', 'closed'].includes(a.status)).length;

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-20 rounded-xl bg-muted/30 animate-pulse" />
        <div className="grid grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="h-20 rounded-lg bg-muted/20 animate-pulse" />)}
        </div>
        {[1,2,3].map(i => <div key={i} className="h-24 rounded-lg border animate-pulse bg-muted/10" />)}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* ── Header ── */}
      <div>
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Inbox className="h-5 w-5 text-amber-500" />
          My Actions
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Corrective actions assigned to you — track progress and close out
        </p>
      </div>

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Open', value: totalOpen, color: 'text-amber-600', bg: 'bg-amber-500/10 border-amber-200 dark:border-amber-800/40', icon: CircleDot },
          { label: 'Overdue', value: overdue, color: overdue > 0 ? 'text-red-600' : 'text-slate-500', bg: overdue > 0 ? 'bg-red-500/10 border-red-200 dark:border-red-800/40' : 'bg-slate-500/5 border-slate-200 dark:border-slate-700', icon: AlertTriangle },
          { label: 'Due Soon', value: dueSoon, color: dueSoon > 0 ? 'text-amber-600' : 'text-slate-500', bg: dueSoon > 0 ? 'bg-amber-500/10 border-amber-200 dark:border-amber-800/40' : 'bg-slate-500/5 border-slate-200 dark:border-slate-700', icon: Timer },
          { label: 'Completed', value: completedCount, color: 'text-emerald-600', bg: 'bg-emerald-500/10 border-emerald-200 dark:border-emerald-800/40', icon: CheckCircle2 },
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className={`rounded-xl border p-4 ${stat.bg}`}>
              <div className="flex items-center justify-between">
                <Icon className={`h-4 w-4 ${stat.color} opacity-70`} />
                <span className={`text-2xl font-bold tabular-nums ${stat.color}`}>{stat.value}</span>
              </div>
              <p className="text-xs font-medium text-muted-foreground mt-1 uppercase tracking-wider">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search actions, findings, audit reference..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {[
            { key: 'open', label: 'Open', count: totalOpen },
            { key: 'all', label: 'All', count: actions.length },
            { key: 'completed', label: 'Completed' },
            { key: 'closed', label: 'Closed' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`
                inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap
                border transition-all duration-150
                ${statusFilter === f.key
                  ? 'bg-amber-500/10 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700 shadow-sm'
                  : 'bg-background text-muted-foreground border-border hover:border-amber-300 hover:text-foreground'
                }
              `}
            >
              {f.label}
              {f.count != null && (
                <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  statusFilter === f.key ? 'bg-amber-500/20' : 'bg-muted'
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Action Cards ── */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground font-medium">
            {statusFilter === 'open' ? 'No open actions — you\'re all caught up!' : 'No actions found'}
          </p>
          {statusFilter !== 'all' && (
            <Button variant="outline" className="mt-4" onClick={() => setStatusFilter('all')}>
              View all actions
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(action => {
            const cfg = STATUS_CONFIG[action.status] || STATUS_CONFIG.assigned;
            const priority = PRIORITY_CONFIG[action.priority];
            const due = dueBadge(action.due_date);
            const finding = action.audit_findings;
            const audit = finding?.audits;
            const isClosed = action.status === 'closed';
            const isActionable = cfg.next != null;
            const extReq = extensionRequests[action.id];

            return (
              <Card
                key={action.id}
                className={`group cursor-pointer hover:border-amber-500/50 hover:shadow-md transition-all ${
                  isClosed ? 'opacity-60' : ''
                } ${daysUntilDue(action.due_date) < 0 && !isClosed ? 'border-l-4 border-l-red-500' : ''}`}
                onClick={() => { setSelectedAction(action); setNotes(''); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Status dot */}
                    <div className={`mt-1.5 h-3 w-3 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ${cfg.dot}`} />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm truncate">{action.action_title}</h3>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${cfg.color}`}>
                          {cfg.label}
                        </Badge>
                        {priority && (
                          <Badge className={`text-[10px] shrink-0 ${priority.color}`}>
                            {priority.label}
                          </Badge>
                        )}
                      </div>

                      {action.action_description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{action.action_description}</p>
                      )}

                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {/* Finding reference */}
                        {finding?.finding_title && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                            <AlertTriangle className="h-3 w-3" />
                            <span className="truncate max-w-[200px]">{finding.finding_title}</span>
                          </span>
                        )}
                        {/* Audit reference */}
                        {audit?.audit_reference && (
                          <span className="font-mono text-[11px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                            {audit.audit_reference}
                          </span>
                        )}
                        {/* Action type */}
                        <span className="text-[11px] text-muted-foreground">
                          {ACTION_TYPE_LABELS[action.action_type] || action.action_type}
                        </span>
                      </div>
                    </div>

                    {/* Right Side — Due & CTA */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {due && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${due.cls}`}>
                          <CalendarDays className="h-3 w-3" />
                          {due.label}
                        </span>
                      )}
                      {action.due_date && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          Due {new Date(action.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {/* Extension request status indicator */}
                      {extReq && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                          extReq.status === 'pending'
                            ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/40'
                            : extReq.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/40'
                              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40'
                        }`}>
                          <CalendarClock className="h-3 w-3" />
                          {extReq.status === 'pending' ? 'Ext. Pending' : extReq.status === 'approved' ? 'Ext. Approved' : 'Ext. Rejected'}
                        </span>
                      )}
                      {isActionable && (
                        <span className="text-[10px] text-amber-600 font-semibold group-hover:text-amber-700 flex items-center gap-0.5 transition-colors">
                          {cfg.nextLabel} <ChevronRight className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Action Detail / Close-Out Dialog ── */}
      <Dialog open={!!selectedAction} onOpenChange={o => { if (!o) setSelectedAction(null); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          {selectedAction && (() => {
            const action = selectedAction;
            const cfg = STATUS_CONFIG[action.status] || STATUS_CONFIG.assigned;
            const StatusIcon = cfg.icon;
            const priority = PRIORITY_CONFIG[action.priority];
            const due = dueBadge(action.due_date);
            const finding = action.audit_findings;
            const audit = finding?.audits;
            const isActionable = cfg.next != null;
            const extReqDetail = extensionRequests[action.id];
            const canRequestExtension = isActionable && (!extReqDetail || extReqDetail.status !== 'pending');

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base pr-6">
                    <StatusIcon className="h-5 w-5 text-amber-500 shrink-0" />
                    <span className="truncate">{action.action_title}</span>
                  </DialogTitle>
                  <DialogDescription className="sr-only">Action details and close-out</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                  {/* Status + Priority badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className={`${cfg.color}`}>{cfg.label}</Badge>
                    {priority && <Badge className={priority.color}>{priority.label}</Badge>}
                    {due && (
                      <Badge variant="outline" className={due.cls}>
                        <CalendarDays className="h-3 w-3 mr-1" /> {due.label}
                      </Badge>
                    )}
                    {action.requires_verification && (
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-700 border-purple-300">
                        <Shield className="h-3 w-3 mr-1" /> Verification Required
                      </Badge>
                    )}
                  </div>

                  {/* Progress Tracker */}
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3 sm:p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Workflow Progress</p>
                    <div className="flex items-center gap-0">
                      {['assigned', 'in_progress', 'completed', ...(action.requires_verification ? ['verified'] : []), 'closed'].map((step, idx, arr) => {
                        const isComplete = arr.indexOf(action.status) >= idx || action.status === 'closed';
                        const isCurrent = action.status === step;
                        return (
                          <React.Fragment key={step}>
                            <div className={`flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full text-[8px] sm:text-[9px] font-bold shrink-0 transition-all ${
                              isCurrent
                                ? 'bg-amber-500 text-white ring-2 ring-amber-300 ring-offset-1 sm:ring-offset-2 ring-offset-background'
                                : isComplete
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                            }`}>
                              {isComplete && !isCurrent ? <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : idx + 1}
                            </div>
                            {idx < arr.length - 1 && (
                              <div className={`flex-1 h-0.5 min-w-[8px] sm:min-w-[16px] ${isComplete ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-slate-700'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-1.5">
                      {['assigned', 'in_progress', 'completed', ...(action.requires_verification ? ['verified'] : []), 'closed'].map(step => (
                        <span key={step} className="text-[8px] text-muted-foreground text-center flex-1 leading-tight">
                          {STATUS_CONFIG[step]?.label?.split(' ')[0]}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</Label>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3 text-sm whitespace-pre-wrap break-words">
                      {action.action_description || 'No description provided'}
                    </div>
                  </div>

                  {/* Meta Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Due Date</p>
                      <p className="text-sm font-medium">
                        {action.due_date ? new Date(action.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Action Type</p>
                      <p className="text-sm font-medium">{ACTION_TYPE_LABELS[action.action_type] || action.action_type}</p>
                    </div>
                  </div>

                  {/* Finding & Audit Context */}
                  {finding && (
                    <div className="rounded-lg border bg-slate-50 dark:bg-slate-800/50 p-3 space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Related Finding</p>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${
                          finding.finding_type === 'non_conformity' || finding.finding_type === 'major_non_conformity'
                            ? 'text-red-500' : 'text-amber-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium">{finding.finding_title || finding.finding_description?.slice(0, 100)}</p>
                          {audit && (
                            <div className="flex items-center gap-2 mt-1">
                              {audit.audit_reference && (
                                <span className="font-mono text-[10px] bg-muted/50 px-1.5 py-0.5 rounded text-muted-foreground">
                                  {audit.audit_reference}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground">{audit.audit_title}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Completion notes (if already completed) */}
                  {action.completion_notes && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Completion Notes</Label>
                      <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/40 p-3 text-sm whitespace-pre-wrap">
                        {action.completion_notes}
                      </div>
                    </div>
                  )}

                  {/* Verification notes (if verified) */}
                  {action.verification_notes && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Verification Notes</Label>
                      <div className="rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/40 p-3 text-sm whitespace-pre-wrap">
                        {action.verification_notes}
                      </div>
                    </div>
                  )}

                  {/* Extension Request Section */}
                  {extReqDetail && (
                    <div className={`rounded-lg border p-3 space-y-2 ${
                      extReqDetail.status === 'pending'
                        ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/40'
                        : extReqDetail.status === 'approved'
                          ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40'
                          : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/40'
                    }`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <CalendarClock className="h-3.5 w-3.5" />
                          Extension Request
                        </p>
                        <Badge variant="outline" className={`text-[10px] ${
                          extReqDetail.status === 'pending'
                            ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400'
                            : extReqDetail.status === 'approved'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {extReqDetail.status === 'pending' ? 'Awaiting Review' : extReqDetail.status === 'approved' ? 'Approved' : 'Rejected'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Original due:</span>{' '}
                          <span className="font-medium">
                            {extReqDetail.original_due_date ? new Date(extReqDetail.original_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Requested:</span>{' '}
                          <span className="font-medium">
                            {new Date(extReqDetail.requested_due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground italic">&ldquo;{extReqDetail.reason}&rdquo;</p>
                      {extReqDetail.review_notes && (
                        <p className="text-xs mt-1">
                          <span className="font-semibold">Review notes:</span> {extReqDetail.review_notes}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action Input (when status can advance) */}
                  {isActionable && (
                    <div className="space-y-2 pt-2 border-t">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {action.status === 'in_progress' ? 'Completion Notes *' :
                         action.status === 'assigned' ? 'Notes (optional)' :
                         'Close-Out Notes (optional)'}
                      </Label>
                      <Textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder={
                          action.status === 'in_progress'
                            ? 'Describe what was done to resolve this action...'
                            : action.status === 'assigned'
                              ? 'Any initial notes...'
                              : 'Final close-out comments...'
                        }
                        rows={3}
                        className="text-sm resize-none"
                      />
                    </div>
                  )}
                </div>

                <DialogFooter className="flex flex-col gap-2 pt-2">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
                    {isActionable && (
                      <Button
                        onClick={() => handleAdvanceStatus(action, cfg.next)}
                        disabled={saving || (action.status === 'in_progress' && !notes.trim())}
                        className={`w-full sm:w-auto order-first sm:order-last ${
                          action.status === 'in_progress'
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : action.status === 'verified'
                              ? 'bg-slate-700 hover:bg-slate-800 text-white'
                              : 'bg-amber-600 hover:bg-amber-700 text-white'
                        }`}
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <ArrowRight className="h-4 w-4 mr-2" />
                        )}
                        {cfg.nextLabel}
                      </Button>
                    )}
                    {canRequestExtension && (
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto text-blue-700 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20"
                        onClick={() => {
                          setSelectedAction(null);
                          openExtensionDialog(action);
                        }}
                      >
                        <CalendarClock className="h-4 w-4 mr-1.5" />
                        Request Extension
                      </Button>
                    )}
                    <Button variant="outline" className="w-full sm:w-auto" onClick={() => setSelectedAction(null)}>
                      {isActionable ? 'Cancel' : 'Close'}
                    </Button>
                  </div>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Extension Request Dialog ── */}
      <Dialog open={showExtensionDialog} onOpenChange={o => { if (!o) { setShowExtensionDialog(false); setExtensionAction(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-5 w-5 text-blue-500 shrink-0" />
              Request Deadline Extension
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Submit a request to extend the due date for this action. Your manager will review and approve or reject.
            </DialogDescription>
          </DialogHeader>

          {extensionAction && (
            <div className="space-y-4">
              {/* Action context */}
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3 space-y-1">
                <p className="text-sm font-semibold">{extensionAction.action_title}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    Current due: {extensionAction.due_date
                      ? new Date(extensionAction.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                      : 'Not set'}
                  </span>
                </div>
              </div>

              {/* New due date */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Requested New Due Date *
                </Label>
                <Input
                  type="date"
                  value={extensionDate}
                  min={extensionAction.due_date || new Date().toISOString().slice(0, 10)}
                  onChange={e => setExtensionDate(e.target.value)}
                  className="text-sm"
                />
                {extensionDate && extensionAction.due_date && (
                  <p className="text-xs text-muted-foreground">
                    {Math.ceil((new Date(extensionDate) - new Date(extensionAction.due_date)) / 86400000)} day(s) extension from current due date
                  </p>
                )}
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Reason for Extension *
                </Label>
                <Textarea
                  value={extensionReason}
                  onChange={e => setExtensionReason(e.target.value)}
                  placeholder="Explain why additional time is needed..."
                  rows={3}
                  className="text-sm resize-none"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button variant="outline" onClick={() => { setShowExtensionDialog(false); setExtensionAction(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestExtension}
              disabled={submittingExtension || !extensionDate || !extensionReason.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submittingExtension ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
